import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { AUTH, REPORTS } from "@/constants/testIds";
import BrandLogo from "@/components/BrandLogo";
import SelfieAvatar from "@/components/SelfieAvatar";
import LanguageSelector from "@/components/LanguageSelector";
import { SignOut, ChartLineUp, Info, Question, WhatsappLogo } from "@phosphor-icons/react";

export default function AppHeader() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const selfiePath = user?.identity?.selfie_storage_path || "";

  return (
    <header className="lele-glass sticky top-0 z-40">
      <div className="mx-auto max-w-6xl px-5 md:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="group">
          <BrandLogo size={38} showWordmark />
        </Link>
        <div className="flex items-center gap-3">
          {user && (
            <>
              <Link
                to="/reports"
                data-testid={REPORTS.headerLink}
                className="hidden sm:flex items-center gap-1.5 text-sm text-neutral-400 hover:text-neutral-100 transition-colors"
              >
                <ChartLineUp size={16} weight="duotone" className="text-indigo-400" />
                {t("header.reports")}
              </Link>
              <Link
                to="/about"
                data-testid="header-about-link"
                className="hidden sm:flex items-center gap-1.5 text-sm text-neutral-400 hover:text-neutral-100 transition-colors"
              >
                <Info size={16} weight="duotone" className="text-fuchsia-400" />
                {t("header.about")}
              </Link>
              <Link
                to="/support"
                data-testid="header-support-link"
                className="hidden sm:flex items-center gap-1.5 text-sm text-neutral-400 hover:text-neutral-100 transition-colors"
              >
                <Question size={16} weight="duotone" className="text-emerald-400" />
                {t("header.support")}
              </Link>
              <a
                href="https://wa.me/917075521016"
                target="_blank"
                rel="noreferrer"
                data-testid="header-whatsapp-link"
                title={t("header.needHelp") || "Need help? Chat on WhatsApp"}
                aria-label="WhatsApp support"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm text-emerald-300 hover:text-emerald-100 border border-emerald-400/25 hover:border-emerald-400/50 bg-emerald-500/[0.06] hover:bg-emerald-500/[0.12] rounded-full px-3 py-1.5 transition-colors"
              >
                <WhatsappLogo size={15} weight="fill" />
                <span className="hidden md:inline">{t("header.needHelp")}</span>
              </a>
              <a
                href="https://wa.me/917075521016"
                target="_blank"
                rel="noreferrer"
                data-testid="header-whatsapp-icon"
                aria-label="WhatsApp support"
                className="sm:hidden text-emerald-300 hover:text-emerald-100 transition-colors"
              >
                <WhatsappLogo size={22} weight="fill" />
              </a>
              <LanguageSelector variant="header" className="hidden sm:block" />
              <Link
                to="/profile"
                data-testid="header-profile-link"
                className="hidden md:flex items-center gap-2.5 leading-tight group cursor-pointer"
                title={t("header.profile")}
              >
                <div className="flex flex-col items-end">
                  <span className="text-sm text-neutral-200 group-hover:text-neutral-50 transition-colors">
                    {user.name}
                  </span>
                  <span className="text-[11px] text-neutral-500 flex items-center gap-1.5">
                    {user.email || user.phone}
                    {user.lele_id && (
                      <span
                        data-testid="header-lele-id"
                        className="inline-flex items-center rounded-full border border-indigo-400/25 bg-indigo-500/10 px-1.5 py-0.5 text-[10px] font-medium text-indigo-200 tracking-wider"
                        title="Your LeLe ID — share with lenders to be found instantly"
                      >
                        {user.lele_id}
                      </span>
                    )}
                  </span>
                </div>
                <SelfieAvatar
                  testId="header-selfie-avatar"
                  path={selfiePath}
                  name={user.name}
                  size={38}
                />
              </Link>
              <Link
                to="/profile"
                data-testid="header-profile-icon"
                aria-label={t("header.profile")}
                className="md:hidden"
              >
                <SelfieAvatar
                  path={selfiePath}
                  name={user.name}
                  size={34}
                  ring={false}
                />
              </Link>
              <button
                data-testid={AUTH.logoutBtn}
                className="lele-btn-ghost text-sm flex items-center gap-1.5"
                onClick={async () => {
                  await logout();
                  nav("/login");
                }}
              >
                <SignOut size={16} weight="bold" />
                {t("common.logout")}
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
